import { Col, Form, Row, notification } from 'antd';
import type { FilterConfig, FilterValues } from 'types';
import { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';

import DataGrid from 'components/theme_components/DataGrid';
import ExportXLS from 'components/theme_components/ExportXLS';
import type { FormInstance } from 'antd/lib/form';
import HeroTitle from 'components/theme_components/HeroTitle';
import React from 'react';
import { findPaymentByAnyRefOrStatusOrDate } from 'services/PaymentServices';

export type PaymentHistoryProps = {
  form: FormInstance;
  color?: string;
  tpauthid: number;
  tpid?: number;
  itemsCompanyHierarchyTree: any[];
  tptype?: string;
  isActive?: boolean;
  onTabActivated?: () => void;
  onNavigateToContract?: (contractNumber: string) => void;
};

const PaymentHistory = (props: PaymentHistoryProps) => {
  const {
    form,
    color,
    tpauthid,
    tpid,
    itemsCompanyHierarchyTree,
    tptype,
    isActive,
    onTabActivated,
  } = props;

  const [filterForm] = Form.useForm();
  const [searchParams, setSearchParams] = useState({});
  const queryClient = useQueryClient();

  const usePayments = (
    searchParams: any,
    tpauthid: number,
    tpid?: number,
    isActive?: boolean
  ) => {
    const mapSearchParamsToAPI = (params: any) => {
      return {
        cireference: params.invoiceNumber || params.sireference || '',
        ctreference: params.contractNumber || params.ctreference || '',
        vin: params.vin2 || params.vin || '',
        payamount: params.amount || '',
        paydate: params.paymentDate || '',
        datemax: params.paymentDateMax || params.paymentDate || '',
        datemin: params.paymentDateMin || params.paymentDate || '',
        payallstat: '',
        paydocreference: params.invoiceNumber || params.sireference || '',
        paymode: '',
        paydirection: '',
        payreference: '',
        paystatus: '',
        sireference: params.invoiceNumber || params.sireference || '',
        tpname: params.productFamily || params.tpname || '',
        tpreference: params.tpreference || '',
        currcode: '',
        paytype: '',
        flagFilters: true,
      };
    };

    const initSearchParams = tpid
      ? { ...searchParams, tpid: tpid }
      : searchParams;
    const apiParams = mapSearchParamsToAPI(initSearchParams);

    return useQuery(
      ['findPaymentByAnyRefOrStatusOrDate', searchParams, tpauthid, tpid],
      () =>
        findPaymentByAnyRefOrStatusOrDate(apiParams)
          .then((res: any) => {
            return res?.data?.list || res?.list || res?.data || [];
          })
          .catch((error) => {
            notification.error({
              description: error.message,
              message: 'Payment Service is not available',
            });
            return [];
          }),
      {
        refetchOnMount: true,
        enabled: isActive !== false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        staleTime: 0,
      }
    );
  };

  const {
    data: paymentData,
    isFetching,
    refetch,
  } = usePayments(searchParams, tpauthid, tpid, isActive);

  useEffect(() => {
    if (isActive) {
      refetch();
      onTabActivated?.();
    }
  }, [isActive, refetch, onTabActivated]);

  useEffect(() => {
    queryClient.invalidateQueries(['findPaymentByAnyRefOrStatusOrDate']);
  }, [tpauthid, tpid, queryClient]);

  useEffect(() => {
    setSearchParams({});
    filterForm.resetFields();
  }, [tpauthid, tpid, filterForm]);

  const filterConfig: FilterConfig[] = [
    {
      key: 'PRCODE',
      label: 'famille produit',
      type: 'select',
    },
    { key: 'invoiceNumber', label: 'N° facture', type: 'text' },
    { key: 'vin', label: 'VIN', type: 'text' },
    { key: 'contractNumber', label: 'N° contrat', type: 'text' },
    {
      key: 'paymentDateMin',
      label: 'Date de paiement (début)',
      type: 'date',
      placeholder: 'Date début',
    },
    {
      key: 'paymentDateMax',
      label: 'Date de paiement (fin)',
      type: 'date',
      placeholder: 'Date fin',
    },
    { key: 'amount', label: 'Montant', type: 'number' },
  ];

  const filterConfigGlobal: FilterConfig[] = [
    { key: 'sireference', label: 'N° facture', type: 'text' },
    { key: 'vin2', label: 'VIN2', type: 'text' },
    { key: 'ctreference', label: 'N° contrat', type: 'text' },
    { key: 'tpname', label: 'tpname', type: 'text' },
    {
      key: 'paymentDateMin',
      label: 'Date de paiement (début)',
      type: 'date',
      placeholder: 'Date début',
    },
    {
      key: 'paymentDateMax',
      label: 'Date de paiement (fin)',
      type: 'date',
      placeholder: 'Date fin',
    },
    { key: 'amount', label: 'Montant', type: 'number' },
    { key: 'tpreference', label: 'RRDI', type: 'text' },
  ];

  const handleFilterSearch = useCallback((values: FilterValues) => {
    const formattedValues = {
      ...values,
      paymentDateMin: values.paymentDateMin
        ? values.paymentDateMin.format('YYYY-MM-DD')
        : '',
      paymentDateMax: values.paymentDateMax
        ? values.paymentDateMax.format('YYYY-MM-DD')
        : '',
    };
    setSearchParams(formattedValues);
  }, []);

  const handleFilterReset = useCallback(() => {
    filterForm.resetFields();
    setSearchParams({});
  }, [filterForm]);

  const columns = [
    {
      title: 'N° facture',
      dataIndex: 'sireference',
      key: 'sireference',
      render: (_: any, record: any) =>
        record.sireference
          ? record.sireference
          : record.sidocreference
          ? record.sidocreference
          : '--',
    },
    {
      title: 'Nom du point de vente',
      dataIndex: 'tpname',
      key: 'tpname',
    },
    {
      title: 'RRDI',
      dataIndex: 'tpreference',
      key: 'tpreference',
    },
    {
      title: 'VIN',
      key: 'vin',
      render: (_: any, record: any) =>
        `${record.vin1 || ''}${record.vin2 ? ` ${record.vin2}` : ''}`,
    },
    {
      title: 'N° contrat',
      dataIndex: 'ctreference',
      key: 'ctreference',
    },
    {
      title: 'Famille produit',
      dataIndex: 'prcodelabel',
      key: 'prcodelabel',
    },
    {
      title: 'Date du paiement',
      dataIndex: 'paydate',
      key: 'paydate',
      sorter: true,
      render: (value: string) =>
        value ? new Date(value).toLocaleDateString('fr-FR') : '',
    },
    {
      title: 'Montant du paiement (€)',
      dataIndex: 'payamount',
      key: 'payamount',
      render: (value: number) =>
        value?.toLocaleString('fr-FR', { minimumFractionDigits: 0 }),
    },
  ];

  const colsXLS = [
    'sireference',
    'sidocreference',
    'tpreference',
    'tpname',
    'vin1',
    'vin2',
    'ctreference',
    'statuslabel',
    'prcodelabel',
    'paydate',
    'payamount',
  ];
  const columnLabels: { [key: string]: string } = {
    sireference: 'N° facture',
    sidocreference: 'N° facture externe',
    tpreference: 'RRDI',
    tpname: 'Nom du point de vente',
    vin1: 'VIN1',
    vin2: 'VIN2',
    ctreference: 'N° contrat',
    statuslabel: 'Statut',
    prcodelabel: 'Famille produit',
    paydate: 'Date du paiement',
    payamount: 'Montant du paiement (€)',
  };

  return (
    <Form form={form} className="bg-color-gray">
      <div className="bg-color-gray">
        <HeroTitle
          form={filterForm}
          color={color || 'purple'}
          title="searchPaymentHistory" 
          display
          showFilter
          filterConfig={!tptype ? filterConfigGlobal : filterConfig}
          onFilterSearch={handleFilterSearch}
          onFilterReset={handleFilterReset}
          className="paymenthistory"
          noTitleOverlay
          itemsCompanyHierarchyTree={itemsCompanyHierarchyTree}
        />
      </div>
      <div className="ut-title-page no-after">
        <Row justify="space-between" align="middle" className="mb-4 mt-6">
          <Col></Col>
          <Col span={5} className="d-flex justify-end align-center">
            <ExportXLS
              data={paymentData || []}
              filename="payment-history"
              columns={colsXLS}
              columnLabels={columnLabels}
            />
          </Col>
        </Row>
        <Row className="w-100 mb-1 d-flex align-center">
          <Col span={24}>
            <Form.Item name="filteredData" initialValue={paymentData}>
              <DataGrid
                columns={columns}
                dataSource={paymentData || []}
                size="large"
                className="custom-data-grid"
                rowKey="id"
                isLoading={isFetching}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} sur ${total}`,
                }}
                scrollInput={{ x: 1000, y: 550 }}
              />
            </Form.Item>
          </Col>
        </Row>
      </div>
    </Form>
  );
};

export default PaymentHistory;
